// frontend/src/components/Product/ProductCard.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import CurrencyDisplay from "../UI/CurrencyDisplay";
import { useAuth } from "../../context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  faCalendarCheck,
  faGlobe,
  faCheckCircle,
  faStar,
  faTractor,
  faBox,
  faRuler,
  faTag,
} from "@fortawesome/free-solid-svg-icons";

// Import product images
import dairyBoostImg from "../../assets/DairyBoost.png";
import mineralSaltImg from "../../assets/Mineral-salt.png";
import growProImg from "../../assets/growpro.png";
import darkImg from "../../assets/dark.png";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// PRODUCT IMAGE MAPPING
// ============================================
const productImages = {
  "rumen-bypass-fat": dairyBoostImg,
  "mineral-salt-block": mineralSaltImg,
  "protein-concentrate": growProImg,
  "feed-supplement-mix": darkImg,
  "kenali-growpro": growProImg,
  "kenali-mineralite": mineralSaltImg,
  "kenali-dairyboost": dairyBoostImg,
  "kenali-feed-supplement-mix": darkImg,
  fallback1: dairyBoostImg,
  fallback2: mineralSaltImg,
  fallback3: growProImg,
  fallback4: darkImg,
};

// ============================================
// MAIN COMPONENT
// ============================================
const ProductCard = ({
  product,
  userType: propUserType,
  farmSize: propFarmSize,
}) => {
  const { user } = useAuth();
  const [imageError, setImageError] = useState(false);

  const userType = propUserType || user?.userType || "customer";
  const farmSize = propFarmSize || user?.farmDetails?.farmSize || "small";
  const isFarmer = userType === "farmer";
  const isGrowPro = product.slug === "kenali-growpro";
  const isMalaysia =
    product.origin === "Malaysia" || product.origin === undefined;

  // ============================================
  // HELPERS
  // ============================================
  const getDisplayPrice = () => {
    if (product.calculatedPrice?.perUnit)
      return product.calculatedPrice.perUnit;
    if (product.pricing?.usd) return product.pricing.usd;
    if (product.basePrice?.usd) return product.basePrice.usd;
    if (product.price) return product.price;
    return 0;
  };

  const displayPrice = getDisplayPrice();
  const discountPercent = product.calculatedPrice?.discountPercent || 0;

  const getImageSrc = () => {
    if (imageError) return null;
    if (product.image) return product.image;
    if (product.images && product.images[0]) return product.images[0];
    if (product.slug && productImages[product.slug])
      return productImages[product.slug];
    if (product._id && productImages[product._id])
      return productImages[product._id];
    return null;
  };

  const imageSrc = getImageSrc();
  const stockStatus =
    product.stockStatus || (product.stock > 0 ? "in_stock" : "out_of_stock");
  const isInStock = stockStatus !== "out_of_stock";
  const hasPreOrder =
    product.isPreOrderAvailable !== false &&
    product.preOrderSizes &&
    product.preOrderSizes.length > 0;

  // ============================================
  // HANDLERS
  // ============================================
  const handleWhatsAppClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(
      `https://wa.me/23231030808?text=Hi%20Kenali%20Enterprise%2C%20I%27m%20interested%20in%20your%20product%3A%20${encodeURIComponent(product.name)}`,
      "_blank",
    );
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <motion.div
      className="product-card"
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={`/products/${product.slug}`} className="product-card-link">
        {/* ========== IMAGE ========== */}
        <div className="product-image-wrapper">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={product.name}
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : (
            <div className="product-image-placeholder">📦</div>
          )}

          {/* Badges */}
          {product.featured && (
            <span className="badge badge-featured">
              <FontAwesomeIcon icon={faStar} /> Featured
            </span>
          )}
          {!isInStock && (
            <span className="badge badge-out-of-stock">Out of Stock</span>
          )}
          {isGrowPro && hasPreOrder && (
            <span className="badge badge-preorder">
              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
            </span>
          )}
          {isMalaysia && (
            <span className="badge badge-origin">
              <FontAwesomeIcon icon={faGlobe} /> MY
            </span>
          )}

          {/* WhatsApp Button */}
          <div
            className="whatsapp-btn"
            onClick={handleWhatsAppClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleWhatsAppClick(e);
              }
            }}
          >
            <FontAwesomeIcon icon={faWhatsapp} />
            Ask
          </div>
        </div>

        {/* ========== BODY ========== */}
        <div className="product-body">
          <h3>
            {product.name}
            {isMalaysia && (
              <span className="origin-tag">
                <FontAwesomeIcon icon={faGlobe} /> MY
              </span>
            )}
          </h3>
          <div className="product-category">
            {product.category?.replace(/-/g, " ")}
          </div>

          <div className="product-price">
            <CurrencyDisplay amountUSD={displayPrice} compact />
            <span className="unit">/{product.unit || "kg"}</span>
            {discountPercent > 0 && (
              <span className="discount-badge">-{discountPercent}%</span>
            )}
          </div>

          {discountPercent > 0 && product.basePrice?.usd && (
            <div className="product-original-price">
              <CurrencyDisplay amountUSD={product.basePrice.usd} compact />
            </div>
          )}

          {/* Available Sizes */}
          {product.availableSizes && product.availableSizes.length > 0 && (
            <div className="size-tags">
              {product.availableSizes.slice(0, 4).map((size) => (
                <span key={size} className="size-tag">
                  {size}kg
                  {product.preOrderSizes?.includes(size) && (
                    <span className="preorder-dot">📋</span>
                  )}
                </span>
              ))}
            </div>
          )}

          {isFarmer && product.farmerMinimumOrder && (
            <div className="farmer-badge">
              <FontAwesomeIcon icon={faTractor} /> Min bulk:{" "}
              {product.farmerMinimumOrder}kg
            </div>
          )}

          {isGrowPro && hasPreOrder && (
            <div className="pre-order-badge">
              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order Available
            </div>
          )}

          {stockStatus === "low_stock" && (
            <div className="low-stock-warning">
              ⚡ Only {product.stock} left
            </div>
          )}

          <div className="product-actions">
            <span className="view-details-btn">View Details →</span>
          </div>
        </div>
      </Link>

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        .product-card {
          background: #ffffff;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
          height: 100%;
          display: flex;
          flex-direction: column;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .product-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
          border-color: #D9A321;
        }

        .product-card-link {
          text-decoration: none;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        /* ========== IMAGE ========== */
        .product-image-wrapper {
          position: relative;
          padding-top: 75%;
          background: #F8F8F8;
          overflow: hidden;
        }

        .product-image-wrapper img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .product-card:hover .product-image-wrapper img {
          transform: scale(1.05);
        }

        .product-image-placeholder {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          color: #D9A321;
        }

        /* ========== BADGES ========== */
        .badge {
          position: absolute;
          padding: 0.15rem 0.5rem;
          border-radius: 10px;
          font-size: 0.55rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          display: flex;
          align-items: center;
          gap: 0.2rem;
          z-index: 2;
        }

        .badge-featured {
          right: 0.5rem;
          top: 0.5rem;
          background: #D9A321;
          color: #ffffff;
        }

        .badge-out-of-stock {
          left: 0.5rem;
          top: 0.5rem;
          background: #dc2626;
          color: #ffffff;
        }

        .badge-preorder {
          left: 0.5rem;
          top: 0.5rem;
          background: #D9A321;
          color: #ffffff;
        }

        .badge-origin {
          right: 0.5rem;
          top: 0.5rem;
          background: rgba(0, 107, 46, 0.85);
          color: #ffffff;
          font-size: 0.5rem;
        }

        /* ========== WHATSAPP BUTTON ========== */
        .whatsapp-btn {
          position: absolute;
          bottom: 0.5rem;
          left: 0.5rem;
          background: #25d366;
          color: #ffffff;
          padding: 0.15rem 0.5rem;
          border-radius: 16px;
          font-size: 0.55rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.2rem;
          z-index: 2;
          box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
          transition: all 0.3s ease;
          cursor: pointer;
          border: none;
        }

        .whatsapp-btn:hover {
          transform: scale(1.05);
          background: #1da851;
        }

        /* ========== BODY ========== */
        .product-body {
          padding: 0.75rem 1rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .product-body h3 {
          font-size: 0.875rem;
          font-weight: 600;
          color: #1F1F1F;
          margin: 0 0 0.15rem;
          line-height: 1.3;
          min-height: 38px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .origin-tag {
          font-size: 0.5rem;
          background: #e8f5e9;
          color: #006B2E;
          padding: 0.05rem 0.3rem;
          border-radius: 8px;
          margin-left: 0.25rem;
          display: inline-flex;
          align-items: center;
          gap: 0.1rem;
        }

        .product-category {
          font-size: 0.65rem;
          color: #5A5A5A;
          margin-bottom: 0.3rem;
          text-transform: capitalize;
        }

        /* ========== PRICE ========== */
        .product-price {
          display: flex;
          align-items: baseline;
          gap: 0.2rem;
          font-size: 1rem;
          font-weight: 700;
          color: #D9A321;
          margin-bottom: 0.15rem;
          flex-wrap: wrap;
        }

        .product-price .unit {
          font-size: 0.65rem;
          font-weight: 400;
          color: #5A5A5A;
        }

        .product-price .discount-badge {
          font-size: 0.55rem;
          background: #16a34a;
          color: #ffffff;
          padding: 0.05rem 0.3rem;
          border-radius: 8px;
          font-weight: 600;
        }

        .product-original-price {
          font-size: 0.65rem;
          color: #5A5A5A;
          text-decoration: line-through;
          margin-bottom: 0.15rem;
        }

        /* ========== SIZES ========== */
        .size-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.2rem;
          margin: 0.15rem 0;
        }

        .size-tag {
          font-size: 0.55rem;
          background: #f0f0f0;
          color: #1F1F1F;
          padding: 0.05rem 0.4rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.1rem;
        }

        .preorder-dot {
          font-size: 0.5rem;
        }

        /* ========== BADGES ========== */
        .farmer-badge {
          font-size: 0.6rem;
          background: #e8f5e9;
          color: #006B2E;
          padding: 0.1rem 0.4rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
          margin: 0.15rem 0;
          width: fit-content;
        }

        .pre-order-badge {
          font-size: 0.6rem;
          background: #fef3c7;
          color: #d97706;
          padding: 0.1rem 0.4rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.2rem;
          margin: 0.15rem 0;
          width: fit-content;
        }

        .low-stock-warning {
          font-size: 0.6rem;
          color: #f59e0b;
          margin: 0.15rem 0;
        }

        /* ========== ACTIONS ========== */
        .product-actions {
          margin-top: auto;
          padding-top: 0.5rem;
        }

        .view-details-btn {
          display: inline-flex;
          align-items: center;
          font-size: 0.7rem;
          font-weight: 500;
          color: #006B2E;
          transition: all 0.3s ease;
        }

        .view-details-btn:hover {
          color: #D9A321;
          transform: translateX(4px);
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .product-image-wrapper {
            padding-top: 65%;
          }
          .product-body h3 {
            font-size: 0.813rem;
            min-height: 34px;
          }
          .product-price {
            font-size: 0.875rem;
          }
        }

        @media (max-width: 480px) {
          .product-image-wrapper {
            padding-top: 60%;
          }
          .product-body {
            padding: 0.5rem 0.75rem;
          }
          .product-body h3 {
            font-size: 0.75rem;
            min-height: 30px;
          }
          .whatsapp-btn {
            font-size: 0.45rem;
            padding: 0.1rem 0.35rem;
          }
          .badge {
            font-size: 0.45rem;
            padding: 0.1rem 0.35rem;
          }
          .product-price {
            font-size: 0.8rem;
          }
          .size-tag {
            font-size: 0.45rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default ProductCard;
